import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Target, Plus, User as UserIcon, LogOut, Star, BarChart3, Settings as SettingsIcon, Home, Wallet, Menu, X, Trophy, Store, ShoppingBag } from "lucide-react";
import { ProBadge, SheriffBadge } from "@/components/ProBadge";
import { SubscriptionModal } from "@/components/SubscriptionModal";
import { NotificationBell } from "@/components/NotificationBell";
import { useState, useEffect } from "react";
import { ROUTES } from "@/config/constants";
import { useStore } from "@/store";
import { trackFunnelEvent } from "@/lib/funnelAnalytics";

export const Header = () => {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const { profile } = useStore();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  
  const isPro = profile?.is_pro || false;
  const signupUrl = `${ROUTES.AUTH}?${new URLSearchParams({ returnTo: ROUTES.POST_BOUNTY, mode: 'signup' })}`;
  const signinUrl = `${ROUTES.AUTH}?${new URLSearchParams({ returnTo: ROUTES.POST_BOUNTY, mode: 'signin' })}`;
  const trackEntry = (intent: 'post' | 'earn') => trackFunnelEvent('entry_intent', { intent, source: 'header' });

  // Secret admin access: Click the logo 5 times quickly
  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount >= 5) {
      setClickCount(0);
      nav('/ops-command');
    }
    
    // Reset count after 2 seconds
    setTimeout(() => setClickCount(0), 2000);
  };

  // Secret keyboard shortcut: Ctrl+Shift+A (or Cmd+Shift+A on Mac)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        nav('/ops-command');
      }
      // Alternative: Triple-tap Alt key
      if (e.altKey && e.detail === 3) {
        nav('/ops-command');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nav]);
  
  return (
    <>
      <motion.header
        initial={{ y: -64, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        className="border-b bg-background/85 backdrop-blur-lg supports-[backdrop-filter]:bg-background/70 sticky top-0 z-40"
      >
        <div className="container flex items-center justify-between h-16 gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2.5 font-bold text-lg sm:text-xl text-foreground cursor-pointer select-none" onClick={handleLogoClick}>
            <motion.span
              whileHover={{ rotate: 90, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] shadow-sm"
            >
              <Target className="h-5 w-5" />
            </motion.span>
            <span className="hidden sm:inline tracking-tight">BountyClaimer</span>
            <span className="sm:hidden">BC</span>
          </Link>
          
          {/* Mobile menu button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="lg:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuOpen ? "mobile-navigation" : undefined}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mobileMenuOpen ? "close" : "open"}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="block"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.span>
            </AnimatePresence>
          </motion.button>
          
          {/* Desktop navigation */}
          <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-1">
            {user ? (
              <>
                {/* Main navigation links */}
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/#feed" aria-label="Earn rewards" onClick={() => trackEntry('earn')}><Home className="h-4 w-4" /><span className="hidden xl:inline text-sm">Earn rewards</span></Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/sell" aria-label="Sell a website"><Store className="h-4 w-4" /><span className="hidden xl:inline text-sm">Sell a site</span></Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/marketplace" aria-label="Marketplace"><ShoppingBag className="h-4 w-4" /><span className="hidden xl:inline text-sm">Marketplace</span></Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/profile" aria-label="Profile"><UserIcon className="h-4 w-4" /><span className="hidden xl:inline text-sm">Profile</span></Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/wall" aria-label="Bounty Wall">
                    <Trophy className="h-4 w-4" />
                    <span className="hidden xl:inline text-sm">Wall</span>
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm" className="gap-1.5 px-2.5">
                  <Link to="/settings" aria-label="Settings"><SettingsIcon className="h-4 w-4" /><span className="hidden xl:inline text-sm">Settings</span></Link>
                </Button>

                {/* Visual divider */}
                <div className="w-px h-5 bg-border mx-1.5" />

                {/* Wallet */}
                <Button asChild variant="outline" size="sm" className="gap-1.5 px-3">
                  <Link to={ROUTES.DEPOSIT} aria-label="Wallet">
                    <Wallet className="h-4 w-4" />
                    <span className="hidden xl:inline text-sm">Wallet</span>
                  </Link>
                </Button>

                {/* Post Bounty - primary CTA */}
                <Button asChild variant="default" size="sm" className="h-9 px-4 py-2 bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] border-none shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5">
                  <Link to={ROUTES.POST_BOUNTY} onClick={() => trackEntry('post')}><Plus className="h-4 w-4 mr-1 fill-current" /> Post a task</Link>
                </Button>

                <NotificationBell />

                {!isPro && (
                  <Button 
                    variant="default" 
                    size="sm"
                    className="h-9 px-4 py-2 text-white border-none shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    <Star className="h-4 w-4 mr-1 fill-current" />
                    Pro
                  </Button>
                )}
                {isPro && (
                  <>
                    <Button asChild variant="ghost" size="sm" className="gap-2">
                      <Link to={ROUTES.ANALYTICS} aria-label="Analytics">
                        <BarChart3 className="h-4 w-4" />
                        <ProBadge size="sm" showTooltip={false} />
                      </Link>
                    </Button>
                    <SheriffBadge size="sm" />
                  </>
                )}

                <Button variant="ghost" size="sm" aria-label="Sign out" onClick={() => { signOut(); nav("/"); }}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm"><Link to="/#feed" onClick={() => trackEntry('earn')}>Earn rewards</Link></Button>
                <Button asChild variant="ghost" size="sm"><Link to="/sell">Sell a site</Link></Button>
                <Button asChild variant="ghost" size="sm"><Link to="/marketplace">Marketplace</Link></Button>
                <Button asChild variant="ghost" size="sm"><Link to={signinUrl}>Sign in</Link></Button>
                <Button asChild variant="outline" size="sm"><Link to={signupUrl}>Create account</Link></Button>
                <Button asChild size="sm" className="bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] hover:from-[hsl(45_90%_50%)] hover:to-[hsl(35_84%_42%)] font-semibold shadow-sm border-none">
                  <Link to={ROUTES.POST_BOUNTY} onClick={() => trackEntry('post')}><Plus className="h-4 w-4 mr-1" />Post a task</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
        
        {/* Mobile navigation menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden border-t bg-background overflow-hidden"
            >
              <motion.nav
                id="mobile-navigation"
                aria-label="Mobile navigation"
                initial={{ y: -12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -12, opacity: 0 }}
                transition={{ duration: 0.22, delay: 0.05 }}
                className="container py-4 space-y-2"
              >
              {user ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {!isPro && (
                      <Button 
                        variant="default" 
                        size="sm"
                        className="h-10 text-white border-none"
                        onClick={() => { setShowUpgradeModal(true); setMobileMenuOpen(false); }}
                      >
                        <Star className="h-4 w-4 mr-1 fill-current" />
                        Pro
                      </Button>
                    )}
                    {isPro && (
                      <>
                        <Button 
                          asChild 
                          variant="ghost" 
                          size="sm"
                          className="h-10"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Link to={ROUTES.ANALYTICS} aria-label="Analytics">
                            <BarChart3 className="h-4 w-4 mr-1" />
                            <ProBadge size="sm" showTooltip={false} />
                          </Link>
                        </Button>
                        <div className="flex items-center justify-center py-1">
                          <SheriffBadge size="sm" />
                        </div>
                      </>
                    )}
                    <Button 
                      asChild 
                      variant="default" 
                      size="sm" 
                      className="h-10 bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] border-none"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to={ROUTES.POST_BOUNTY} onClick={() => trackEntry('post')}><Plus className="h-4 w-4 mr-1" /> Post a task</Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="outline" 
                      size="sm" 
                      className="h-10"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to={ROUTES.DEPOSIT}>
                        <Wallet className="h-4 w-4 mr-1" />
                        Wallet
                      </Link>
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-2 border-t border-border">
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/#feed" onClick={() => trackEntry('earn')}>
                        <Home className="h-5 w-5" />
                        <span className="text-xs">Earn rewards</span>
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/sell">
                        <Store className="h-5 w-5" />
                        <span className="text-xs">Sell a site</span>
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/marketplace">
                        <ShoppingBag className="h-5 w-5" />
                        <span className="text-xs">Marketplace</span>
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/profile">
                        <UserIcon className="h-5 w-5" />
                        <span className="text-xs">Profile</span>
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/wall">
                        <Trophy className="h-5 w-5" />
                        <span className="text-xs">Wall</span>
                      </Link>
                    </Button>
                    <Button 
                      asChild 
                      variant="ghost" 
                      size="sm" 
                      className="h-12 flex flex-col gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Link to="/settings">
                        <SettingsIcon className="h-5 w-5" />
                        <span className="text-xs">Settings</span>
                      </Link>
                    </Button>
                  </div>
                  <div className="pt-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full h-10 flex items-center gap-2"
                      onClick={() => { signOut(); nav("/"); setMobileMenuOpen(false); }}
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="text-sm">Logout</span>
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Button asChild size="lg" className="w-full h-12 bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] font-semibold border-none" onClick={() => setMobileMenuOpen(false)}>
                    <Link to={ROUTES.POST_BOUNTY} onClick={() => trackEntry('post')}><Plus className="h-4 w-4 mr-1" />Post a task</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full h-12" onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/#feed" onClick={() => trackEntry('earn')}>Earn rewards</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full h-12" onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/sell">Sell a site</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full h-12" onClick={() => setMobileMenuOpen(false)}>
                    <Link to="/marketplace">Marketplace</Link>
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button asChild variant="outline" className="h-12" onClick={() => setMobileMenuOpen(false)}><Link to={signupUrl}>Create account</Link></Button>
                    <Button asChild variant="ghost" className="h-12" onClick={() => setMobileMenuOpen(false)}><Link to={signinUrl}>Sign in</Link></Button>
                  </div>
                </>
              )}
              </motion.nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <SubscriptionModal 
        open={showUpgradeModal} 
        onOpenChange={setShowUpgradeModal}
      />
    </>
  );
};
